import React, { useMemo, useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchData } from "../utils/api";
import EventCalendarCompaign from "../components/event/EventCalendarCompaign";
import Loader from "../components/Loader.js";

const EventCalendarCampaignPage = ({ companyId }) => {
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["marketing-event", "all_upcoming_events"],
    queryFn: () => fetchData("api/marketing/all_upcoming_events"),
    refetchOnWindowFocus: false,
    staleTime: 0,
  });

  const eventsItems = data?.events || [];

  const [selectedEventIds, setSelectedEventIds] = useState([]);
  const allIds = useMemo(() => eventsItems.map(e => e.id), [eventsItems]);
  const allSelected = useMemo(() => allIds.length > 0 && selectedEventIds.length === allIds.length, [allIds, selectedEventIds]);

  useEffect(() => {
    // Reset selection if events list changes
    setSelectedEventIds([]);
  }, [allIds.join(',')]);

  const toggleSelect = (id) => {
    setSelectedEventIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    setSelectedEventIds(prev => (prev.length === allIds.length ? [] : allIds));
  };

  const handleGenerate = async () => {
    if (selectedEventIds.length === 0) {
      alert("Please select at least one event");
      return;
    }

    try {
      // Fetch existing company posts
      const postsResponse = await fetch("/company_posts");
      const postsData = await postsResponse.json();

      // Flatten posts (grouped by date) to a single array with event_id/message/scheduled_at
      const existing_posts = [];
      Object.values(postsData).forEach(dayPosts => {
        dayPosts.forEach(post => {
          existing_posts.push({
            event_ids: [post.event_id] || [],
            content: post.description,
            status: post.status,
            media: post.image,
            scheduledAt: post.scheduled_at || post.published_at
          });
        });
      });

      // Pick selected events from eventsItems and map to required shape
      const toYYYYMMDD = (d) => {
        try {
          const dt = new Date(d);
          if (isNaN(dt.getTime())) return "";
          return dt.toISOString().split('T')[0];
        } catch { return ""; }
      };
      const toISO = (d) => {
        try {
          const dt = new Date(d);
          if (isNaN(dt.getTime())) return "";
          return dt.toISOString();
        } catch { return ""; }
      };

      const selected_events = eventsItems
        .filter(e => selectedEventIds.includes(e.id))
        .map(e => ({
          id: e.id,
          externalEventId: e.id,
          name: e.name || "",
          description: e.description || "",
          startDate: toISO(e.start_date),
          endDate: toISO(e.end_date),
          eventUrl: e.ticket_purchase_url || "",
          category: e.activity_names || "",
          location: null,
        }));

      const payload = {
        company_id: companyId,
        selected_events,
        existing_posts
      };

      const resp = await fetch('/company_posts/n8n_generate_posts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
        },
        body: JSON.stringify(payload)
      });

      if (!resp.ok) throw new Error('Failed to send data to n8n');

      // Set flag in sessionStorage to show progress header
      sessionStorage.setItem('socialMediaAI_generating', 'true');
      sessionStorage.setItem('socialMediaAI_generation_start', Date.now().toString());
      
      window.location.href = '/marketing/social_media_ai';
    } catch (err) {
      console.error(err);
      alert('Error preparing or sending data.');
    }
  };

  return (
    <div className="marketing__content cts-container">
      {(isLoading || isFetching) && <Loader />}
      <h2 className="feature-heading text-capitalize text-center">Social Media Campaign</h2>
      <div className="marketing__content__events justify-content-center">
        {/* Sidebar start */}
        <div className="marketing__content__events__sidebar w-75">
          {/* Top action bar with Generate */}
          <div className="event-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h2 className="title">Select Events</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button
                className="btns btn-primarys"
                disabled={selectedEventIds.length === 0}
                onClick={handleGenerate}
                style={selectedEventIds.length === 0 ? { opacity: 0.5, pointerEvents: 'none' } : {}}
              >
                Generate ({selectedEventIds.length})
              </button>
              <button
                type="button"
                className="btns btn-outlines"
                onClick={toggleSelectAll}
              >
                {allSelected ? 'Deselect All' : 'Select All'}
              </button>
            </div>
          </div>

          {eventsItems.length === 0 && (
            <div className="event-header">
              <h2 className="title text-center">No events are available for Social Media</h2>
            </div>
          )}

          <div className="events_container">
            {eventsItems.map((event) => (
              <div
                className={`events_container__item ${selectedEventIds.includes(event.id) ? 'active' : ''}`}
                key={event.id}
                onClick={() => toggleSelect(event.id)}
                style={{
                  alignItems: 'center',
                  cursor: 'pointer'
                }}
              >
                <img
                  src={event.banner_image}
                  alt="event"
                  width="76"
                  height="46"
                  style={{ marginRight: 12 }}
                />
                <div>
                  <h4 className="text-ellipse">{event.name}</h4>
                  <span>{event.event_address}</span> | {" "}
                  <span className="ml-1">{event?.start_date}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* Sidebar end */}
      </div>
    </div>
  );
};

export default EventCalendarCampaignPage;


